import React, { useCallback, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  LinearProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartActionButtonSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';
import { formatDateDisplay } from '../../utils/dateUtils';
const apiUrl = process.env.REACT_APP_API_URL;

function MetricCard({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 50, height: 50 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900, overflowWrap: 'anywhere' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const OwnerList = () => {
  const { t, i18n } = useTranslation();
  const [owners, setOwners] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteOwnerId, setDeleteOwnerId] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const fetchOwners = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');

      const ownersResponse = await axios.get(`${apiUrl}/owners`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setOwners(Array.isArray(ownersResponse.data) ? ownersResponse.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('error_fetching_owners'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleViewClick = (ownerId) => {
    navigate(`/owner-details/${ownerId}`);
  };

  const handleEditClick = (ownerId) => {
    navigate(`/owner-edit/${ownerId}`);
  };

  const handleDeleteClick = (ownerId) => {
    setDeleteOwnerId(ownerId);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    if (confirmationText.toLowerCase() !== 'delete') {
      setError(t('please_type_delete_to_confirm'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/owners/${deleteOwnerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setOwners((current) => current.filter((owner) => owner.owner_id !== deleteOwnerId));
      setSuccess(t('owner_deleted_successfully'));
      setDeleteConfirmationOpen(false);
      setConfirmationText('');
    } catch (err) {
      setError(err?.response?.data?.error || t('error_deleting_owner'));
    }
  };

  const handleAddOwner = () => {
    navigate('/register-owner');
  };

  const handleCloseDialog = () => {
    setDeleteConfirmationOpen(false);
    setConfirmationText('');
  };
  const filteredOwners = useMemo(() => owners.filter((owner) => {
    const fullName = `${owner.first_name || ''} ${
      owner.last_name || ''
    }`.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || String(owner.email || '').toLowerCase().includes(search) || String(owner.phone || '').toLowerCase().includes(search);
  }), [owners, searchTerm]);
  const summary = useMemo(() => ({
    totalOwners: owners.length,
    filteredOwners: filteredOwners.length,
    totalHorses: owners.reduce((sum, owner) => sum + Number(owner.horses_count || 0), 0),
  }), [filteredOwners.length, owners]);
  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedOwners,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredOwners, [searchTerm]);

  // Export the full owners list (filtered by the current search term) to a
  // styled XLSX. Uses the data already loaded in memory — no extra fetch.
  const handleExportAllOwners = () => {
    const rows = filteredOwners;
    if (!rows.length) {
      setError(t('no_owners_to_export') || 'No owners to export.');
      return;
    }

    const columns = [
      { header: '#',                                       key: 'idx',          width: 6  },
      { header: 'Owner Name / اسم المالك',                key: 'full_name',    width: 30 },
      { header: 'Phone / الهاتف',                          key: 'phone',        width: 18 },
      { header: 'Email / البريد الإلكتروني',              key: 'email',        width: 28 },
      { header: 'Address / العنوان',                       key: 'address',      width: 32 },
      { header: 'Horses Count / عدد الخيل',                key: 'horses_count', width: 14 },
    ];

    const data = rows.map((owner, index) => ({
      idx: index + 1,
      full_name: `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || '-',
      phone: owner.phone || '-',
      email: owner.email || '-',
      address: owner.address || '-',
      horses_count: Number(owner.horses_count || 0),
    }));

    // ---- Build worksheet ----
    const ws_data = [];

    // Row 1: title (merged A → F)
    ws_data.push([
      `Owners list — ${data.length} owners / قائمة الملاك — ${data.length} مالك`,
    ]);
    // Row 2: column headers
    ws_data.push(columns.map((c) => c.header));
    // Rows 3+: data
    data.forEach((row) => ws_data.push(columns.map((c) => row[c.key])));

    const ws = XLSX.utils.aoa_to_sheet(ws_data);

    // Merge the title across all columns
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } }];

    // Column widths
    ws['!cols'] = columns.map((c) => ({ wch: c.width }));

    // A4 landscape print
    ws['!pageSetup'] = {
      paperSize: 9,
      orientation: 'landscape',
      fitToWidth: 1,
      fitToHeight: 0,
    };

    // Style: title cell
    if (ws['A1']) {
      ws['A1'].s = {
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: 'E2C67F' } },
      };
    }

    // Style: header row
    for (let c = 0; c < columns.length; c += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: 1, c })];
      if (!cell) continue;
      cell.s = {
        fill: { fgColor: { rgb: 'F9E7B6' } },
        font: { bold: true },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top:    { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left:   { style: 'thin', color: { rgb: 'CCCCCC' } },
          right:  { style: 'thin', color: { rgb: 'CCCCCC' } },
        },
      };
    }

    // Center every data cell
    for (let r = 2; r < ws_data.length; r += 1) {
      for (let c = 0; c < columns.length; c += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell) continue;
        if (!cell.s) cell.s = {};
        cell.s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Owners');

    const excelBuffer = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
      cellStyles: true,
    });

    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `Owners_${stamp}.xlsx`
    );
    setSuccess(t('owners_exported_successfully') || 'Owners exported successfully.');
  };

  const handleExportOwnerHorses = async (owner) => {
    try {
      const token = localStorage.getItem('token');
      // Fetch horses for the selected owner.
      const response = await axios.get(
        `${apiUrl}/owners/${owner.owner_id}/horses-with-details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const horses = response.data;

      if (!Array.isArray(horses) || horses.length === 0) {
        setError(t('owner_horses_export_empty'));
        return;
      }

      // Export column headers.
      const columns = [
        { header: 'Horse Name / اسم الحصان', key: 'name' },
        {
          header: 'Horse Name Arabic / اسم الحصان بالعربية',
          key: 'name_arabic',
        },
        { header: 'Breed / السلالة', key: 'breed' },
        { header: 'Color / اللون', key: 'color' },
        { header: 'Gender / الجنس', key: 'gender' },
        { header: 'Date of Birth / تاريخ الميلاد', key: 'date_of_birth' },
        { header: 'RFID', key: 'rfid_chip_number' },
      ];

      // Prepare export rows.
      const data = horses.map((h) => ({
        name: h.name || '',
        name_arabic: h.name_arabic || '',
        breed: h.breed_english || h.breed || '',
        color: h.color_english || h.color || '',
        gender:
          h.gender === 'S'
            ? 'Stallion / حصان'
            : h.gender === 'M'
            ? 'Mare / فرس'
            : h.gender || '',
        date_of_birth: h.date_of_birth ? formatDateDisplay(h.date_of_birth) : '',
        rfid_chip_number: h.rfid_chip_number || '',
      }));

      // Build worksheet rows.
      const ws_data = [];

      // First row: owner name merged from A to G.
      ws_data.push([
        `Owner: ${owner.first_name || ''} ${owner.last_name || ''} / المالك: ${
          owner.first_name || ''
        } ${owner.last_name || ''}`,
      ]);
      // Second row: headers.
      ws_data.push(columns.map((c) => c.header));
      // Remaining rows: horse data.
      data.forEach((horse) => ws_data.push(columns.map((c) => horse[c.key])));

      // Build worksheet.
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      // Merge the first row from A to G.
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

      // Style the header row.
      for (let i = 0; i < columns.length; i++) {
        const cellRef = XLSX.utils.encode_cell({ r: 1, c: i });
        if (!ws[cellRef]) continue;
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '3F51B5' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }

      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cell_address]) continue;
          if (!ws[cell_address].s) ws[cell_address].s = {};
          ws[cell_address].s.alignment = {
            horizontal: 'center',
            vertical: 'center',
          };
        }
      }

      // Set print-friendly column widths.
      ws['!cols'] = [
        { wch: 20 },
        { wch: 28 },
        { wch: 16 },
        { wch: 14 },
        { wch: 12 },
        { wch: 20 },
        { wch: 24 },
      ];

      // A4 landscape print setup.
      ws['!pageSetup'] = {
        paperSize: 9, // A4
        orientation: 'landscape',
        fitToWidth: 1,
        fitToHeight: 0,
      };

      for (let c = 0; c < columns.length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: 1, c }); // row 2 = r:1
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: { fgColor: { rgb: 'F9E7B6' } }, 
            font: { bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'CCCCCC' } },
              bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
              left: { style: 'thin', color: { rgb: 'CCCCCC' } },
              right: { style: 'thin', color: { rgb: 'CCCCCC' } },
            },
          };
        }
      }

      // Center all cells.
      for (let R = 0; R <= horses.length; R++) {
        for (let C = 0; C < columns.length; C++) {
          const cell = ws[XLSX.utils.encode_cell({ r: R + 1, c: C })];
          if (cell) {
            if (!cell.s) cell.s = {};
            cell.s.alignment = {
              horizontal: 'center',
              vertical: 'center',
              wrapText: true,
            };
          }
        }
      }

      // Style the owner title cell.
      if (ws['A1']) {
        ws['A1'].s = {
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
          font: { bold: true, sz: 14 },
          fill: { fgColor: { rgb: 'E2C67F' } },
        };
      }

      // Build workbook.
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Horses');

      // Export workbook.
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
      });
      saveAs(
        new Blob([excelBuffer], { type: 'application/octet-stream' }),
        `Horses_of_${owner.first_name}_${owner.last_name}.xlsx`
      );
    } catch (err) {
      setError(err?.response?.data?.error || t('owner_horses_export_error'));
    }
  };

  return (
    <Box
      className="smart-page-container"
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
    >
      <SettingsPageHeader
        title={t('owners', 'Owners')}
        subtitle={t('owners_page_hint', 'Manage owners and keep each profile connected to its horses.')}
        count={filteredOwners.length}
        actions={(
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              onClick={handleExportAllOwners}
              startIcon={<FileDownloadIcon />}
              disabled={!filteredOwners.length}
              sx={{
                borderColor: COLORS.line,
                color: COLORS.text,
                fontWeight: 800,
                '&:hover': { borderColor: COLORS.gold, bgcolor: COLORS.cardSoft },
              }}
            >
              {t('export_owners_excel', 'Export Excel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleAddOwner}
              startIcon={<AddIcon />}
              sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
            >
              {t('add_owner', 'Add owner')}
            </Button>
          </Stack>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <MetricCard icon={<VisibilityIcon />} label={t('owners')} value={summary.totalOwners} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard icon={<SearchIcon />} label={t('filtered_records')} value={summary.filteredOwners} color={COLORS.blue} soft={COLORS.blueSoft} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard icon={<ListIcon />} label={t('horses_count')} value={summary.totalHorses} color={COLORS.green} soft={COLORS.greenSoft} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
        <TextField
          label={t('search_by_owner_name')}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: i18n.language === 'ar' ? 0 : 1, ml: i18n.language === 'ar' ? 1 : 0 }} /> }}
          sx={{ maxWidth: 420, width: '100%' }}
        />
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center">#</TableCell>
                <TableCell align="center">{t('owner_name')}</TableCell>
                <TableCell align="center">{t('horses_count')}</TableCell>
                <TableCell align="center">{t('phone')}</TableCell>
                <TableCell align="center">{t('email')}</TableCell>
                <TableCell align="center">{t('address')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOwners.map((owner, index) => (
                <TableRow key={owner.owner_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: i18n.language === 'ar' ? 2 : 1,
                      }}
                    >
                      <Avatar sx={{ bgcolor: COLORS.goldSoft, color: COLORS.gold, mr: i18n.language === 'ar' ? 0 : 2, ml: i18n.language === 'ar' ? 2 : 0 }}>
                        {owner.first_name ? owner.first_name.charAt(0) : '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">
                          {`${owner.first_name || ''} ${owner.last_name || ''}`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{owner.horses_count}</TableCell>
                  <TableCell align="center">{owner.phone || '-'}</TableCell>
                  <TableCell align="center">{owner.email || '-'}</TableCell>
                  <TableCell align="center">{owner.address || '-'}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('view_details')}>
                        <IconButton onClick={() => handleViewClick(owner.owner_id)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}><VisibilityIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('edit_owner')}>
                        <IconButton onClick={() => handleEditClick(owner.owner_id)} sx={smartActionButtonSx(COLORS.gold, COLORS.goldSoft)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton onClick={() => handleDeleteClick(owner.owner_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}><DeleteIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('owner_horses')}>
                        <IconButton onClick={() => navigate(`/OwnerHorsesList/${owner.owner_id}`)} sx={smartActionButtonSx(COLORS.warning, COLORS.warningSoft)}><ListIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('export_owner_horses_excel')}>
                        <IconButton onClick={() => handleExportOwnerHorses(owner)} sx={smartActionButtonSx(COLORS.green, COLORS.greenSoft)}><FileDownloadIcon /></IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredOwners.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: COLORS.muted }}>
                    {searchTerm ? t('no_search_results') : t('no_owners_found')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination
          count={filteredOwners.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog
        open={deleteConfirmationOpen}
        onClose={handleCloseDialog}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          {t('confirm_deletion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('please_type_delete_to_confirm')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="confirmationText"
            label={t('type_delete')}
            fullWidth
            variant="standard"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnerList;

